import { Box, FormControl, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CompleteOperationType, StartOperationType } from '../../../../../../../model/alerts.model';
import { ModalDispatch, ModalState, actions } from '../../../../../../../model/documents.model';
import { DocumentsService } from '../../../../../../../service/documents.service';
import zeplinColor from '../../../../../../../theme/Color';
import CustomButton from '../../../../../../shared/CustomButton/CustomButton';
import CustomCard from '../../../../../../shared/CustomCard/CustomCard';
import CustomModal from '../../../../../../shared/CustomModal/CustomModal';

export default function RenameModal({
  isModalOpen,
  dispatch,
  fileName,
  fileId,
  fetchDocuments,
  startOperation,
  completeOperation,
}: {
  isModalOpen: ModalState;
  dispatch: ModalDispatch;
  fileName: string;
  fileId: string;
  fetchDocuments: () => Promise<void>;
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}) {
  const [name, setName] = useState(fileName);
  const client = useLocation();
  const clientId = client.state?.client.AccountId;

  const handleRenameFile = async () => {
    startOperation('rename', 'Renaming...');
    try {
      await DocumentsService.renameDocument(clientId, fileId, name);
      await fetchDocuments();
      dispatch({ type: actions.closeRenameModal });
      completeOperation('rename', 'File name updated');
    } catch (error) {
      completeOperation('rename', 'Failed to rename file', true);
    }
  };

  return (
    <CustomModal
      handleClose={() => dispatch({ type: actions.closeRenameModal })}
      open={isModalOpen.renameModalOpen}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CustomCard
          styles={{
            borderRadius: '14px',
            padding: { xs: '20px', xl: '24px' },
            backgroundColor: zeplinColor.Neutral1,
            boxShadow: 'rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '22px', xl: '32px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '10px', xl: '16px' },
              }}
            >
              <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                Rename File
              </Typography>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel
                  id='demo-multiple-name-label'
                  sx={{
                    '&.Mui-focused': { color: zeplinColor.Neutral9 },
                  }}
                >
                  File Name
                </InputLabel>
                <OutlinedInput
                  onChange={(e) => setName(e.target.value)}
                  label='File Name'
                  defaultValue={fileName}
                  sx={{
                    width: '300px',
                    borderRadius: '14px',
                  }}
                />
              </FormControl>
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                gap: { xs: '10px', xl: '16px' },
              }}
            >
              <CustomButton
                disabled={false}
                onClick={() => dispatch({ type: actions.closeRenameModal })}
                variant='outlined'
                color={'primary'}
                buttonVariant='responsiveButtonMedium'
                style={{
                  flex: 1,
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: '32px', xl: '48px' },
                  padding: { xs: '7px 12px', xl: '14px 24px' },
                  '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral3 },
                  display: 'flex',
                  border: `1px solid ${zeplinColor.BasicBlack}`,
                  alignItems: 'center',
                  '&:hover': { backgroundColor: zeplinColor.Neutral3 },
                }}
                textStyle={{ color: zeplinColor.BasicBlack }}
                text={'Cancel'}
              />
              <CustomButton
                disabled={false}
                onClick={handleRenameFile}
                variant='contained'
                color={'primary'}
                buttonVariant='responsiveButtonMedium'
                style={{
                  flex: 1,
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: '32px', xl: '48px' },
                  padding: { xs: '7px 12px', xl: '14px 24px' },
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                  display: 'flex',
                  alignItems: 'center',
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={'Save'}
              />
            </Box>
          </Box>
        </CustomCard>
      </Box>
    </CustomModal>
  );
}
